import React, { forwardRef, ForwardedRef, useRef, useCallback, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { searchHighlights, HighlightItem, getUsernameById, getTagNameById, HighlightHashtagItem } from '../../../api/highlightServices';
import './ContentEditable.css';
import HighlightTooltip from '../../home/PlaygroundTab/Post/HighlightTooltip';
import HighlightHashtagTooltip from '../../home/PlaygroundTab/Post/HighlightHashtagTooltip';
import { searchHighlightsHashtag } from '../../../api/highlightServices';

export type HighlightData = {
  highlight_type: 'mention' | 'hashtag';
  highlight_text: string;
  mention_type: 'user' | 'brand' | 'tiktok' | 'instagram' | '';
  text: string;
  data?: any;
};

interface ContentEditableProps {
  text: string;
  highlights?: HighlightData[];
  onChange: (newText: string) => void;
  onHighlightSelect?: (
    selectedText: string,
    highlightType: 'mention' | 'hashtag',
    mentionType: 'user' | 'brand' | 'tiktok' | 'instagram' | ''
  ) => void;
  onHighlightsChange?: (highlights: HighlightData[]) => void;
  placeholder?: string;
  onMentionSearch?: (query: string) => Promise<any[]>;
}

const ContentEditable = forwardRef<HTMLDivElement, ContentEditableProps>(
  (
    {
      text,
      highlights = [],
      onChange,
      onHighlightSelect,
      onHighlightsChange,
      onMentionSearch,
      placeholder = "What's sparkling today?",
    },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [showHighlightTooltip, setShowHighlightTooltip] = useState(false);
    const [highlightQuery, setHighlightQuery] = useState('');
    const [highlightPosition, setHighlightPosition] = useState({ top: 0, left: 0 });
    const [highlightItems, setHighlightItems] = useState<HighlightItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentHighlightType, setCurrentHighlightType] = useState<'mention' | 'hashtag'>('mention');
    const [displayText, setDisplayText] = useState(''); // HTML content for contentEditable
    const [apiText, setApiText] = useState(''); // Text with markers for API
    const [hashtagItems, setHashtagItems] = useState<HighlightHashtagItem[]>([]);

    // Debounced search handler
    const debouncedSearch = useCallback(
      debounce(async (query: string, type: 'mention' | 'hashtag') => {
        if (query.length > 0) {
          setIsLoading(true);
          try {
            if (type === 'hashtag') {
              const hashtags = await searchHighlightsHashtag(query);
              setHashtagItems(hashtags);
            } else {
              const highlights = await searchHighlights(query);
              setHighlightItems(highlights);
            }
          } catch (error) {
            console.error('Error searching:', error);
            type === 'hashtag' ? setHashtagItems([]) : setHighlightItems([]);
          } finally {
            setIsLoading(false);
          }
        } else {
          type === 'hashtag' ? setHashtagItems([]) : setHighlightItems([]);
        }
      }, 300),
      []
    );

    const isNodeInsideHighlightSpan = (node: Node): boolean => {
      let currentNode = node;
      while (currentNode && currentNode !== contentRef.current) {
        if (
          currentNode.nodeType === Node.ELEMENT_NODE &&
          ((currentNode as Element).classList.contains('mention') ||
            (currentNode as Element).classList.contains('hashtag'))
        ) {
          return true;
        }
        currentNode = currentNode.parentNode!;
      }
      return false;
    };

    const getTextBeforeCursor = (range: Range): string => {
      let text = '';
      let currentNode = range.startContainer;
      let offset = range.startOffset;

      while (currentNode && currentNode !== contentRef.current) {
        if (currentNode.nodeType === Node.TEXT_NODE) {
          if (!isNodeInsideHighlightSpan(currentNode)) {
            const nodeText = currentNode.textContent!.substring(0, offset);
            text = nodeText + text;
          } else {
            // Stop if inside a mention or hashtag span
            break;
          }
        }
        // Move to the previous sibling or parent node
        if (currentNode.previousSibling) {
          currentNode = currentNode.previousSibling;
          offset = (currentNode as Text).textContent?.length || 0;
        } else {
          const parent = currentNode.parentNode;
          if (!parent) break;
          currentNode = parent;
          offset = 0;
        }
      }
      return text;
    };

    const getCaretCoordinates = (range: Range) => {
      const rect = range.getBoundingClientRect();
      const contentRect = contentRef.current?.getBoundingClientRect();

      if (contentRect) {
        return {
          top: rect.bottom - contentRect.top + 5,
          left: Math.max(0, rect.left - contentRect.left),
        };
      }

      return { top: 0, left: 0 };
    };

    const findHighlightStartOffset = (range: Range): number => {
      const textBeforeCursor = getTextBeforeCursor(range);
      const mentionMatch = textBeforeCursor.match(/@([a-zA-Z0-9._-]*)$/);
      const hashtagMatch = textBeforeCursor.match(/#([a-zA-Z0-9._-]*)$/);

      if (mentionMatch) {
        console.log('mention match:', mentionMatch[0]);
        return textBeforeCursor.length - mentionMatch[0].length;
      } else if (hashtagMatch) {
        console.log('hashtag match:', hashtagMatch[0]);
        return textBeforeCursor.length - hashtagMatch[0].length;
      }
      return range.startOffset;
    };

    const handleHighlightSelect = (
      highlight_text: string,
      highlight_type: 'mention' | 'hashtag',
      mention_type: 'user' | 'brand' | 'tiktok' | 'instagram' | '',
      user_id?: number,
      tag_id?: number
    ) => {
      if (!contentRef.current) return;

      const selection = window.getSelection();
      if (!selection?.rangeCount) return;

      const range = selection.getRangeAt(0);
      const textNode = range.startContainer;

      console.log('Current node type:', textNode.nodeType);
      console.log('Current node content:', textNode.textContent);
      console.log('Current node parent:', textNode.parentNode);

      if (textNode.nodeType === Node.TEXT_NODE) {
        const textContent = textNode.textContent || '';
        const cursorPosition = range.startOffset;
        
        // Find the start and end of the current mention/hashtag
        let startPosition = cursorPosition;
        let endPosition = cursorPosition;
        
        // Search backwards for the start of the mention/hashtag
        while (startPosition > 0 && textContent[startPosition - 1] !== ' ' && textContent[startPosition - 1] !== '\n') {
          startPosition--;
        }

        // If we found a space before the mention, include it in the deletion
        if (startPosition > 0 && textContent[startPosition - 1] === ' ') {
          startPosition--;
        }
        
        // Search forwards for the end of the mention/hashtag
        while (endPosition < textContent.length && textContent[endPosition] !== ' ' && textContent[endPosition] !== '\n') {
          endPosition++;
        }

        const deleteRange = document.createRange();
        deleteRange.setStart(textNode, startPosition);
        deleteRange.setEnd(textNode, endPosition);
        
        const textToDelete = textContent.substring(startPosition, endPosition);
        console.log('Text to delete:', textToDelete);

        // Remove leading/trailing spaces for the check
        const trimmedTextToDelete = textToDelete.trim();
        const shouldProcessHighlight = (highlight_type === 'hashtag' && trimmedTextToDelete.startsWith('#')) ||
                                     (highlight_type === 'mention' && trimmedTextToDelete.startsWith('@'));

        if (shouldProcessHighlight) {
          // Delete the existing input including # or @
          deleteRange.deleteContents();

          // Create the highlight element
          let highlightElement;
          if (highlight_type === 'hashtag') {
            highlightElement = document.createElement('span');
            highlightElement.className = 'hashtag';
            highlightElement.textContent = `#${highlight_text}`;
            highlightElement.contentEditable = 'false';
          } else if (highlight_type === 'mention') {
            if (mention_type === 'tiktok') {
              highlightElement = document.createElement('span');
              highlightElement.className = 'mention';
              highlightElement.textContent = `@${highlight_text}`;
              highlightElement.setAttribute('data-mention-type', 'tiktok');
              highlightElement.contentEditable = 'false';
            } else if (mention_type === 'instagram') {
              highlightElement = document.createElement('span');
              highlightElement.className = 'mention';
              highlightElement.textContent = `@${highlight_text}`;
              highlightElement.setAttribute('data-mention-type', 'instagram');
              highlightElement.contentEditable = 'false';
            } else if (user_id) {
              highlightElement = document.createElement('span');
              highlightElement.className = 'mention';
              highlightElement.textContent = `@${highlight_text}`;
              highlightElement.setAttribute('data-user-id', user_id.toString());
              highlightElement.contentEditable = 'false';
            }
          }

          if (highlightElement) {
            // Insert the highlight element
            deleteRange.insertNode(highlightElement);
            
            // Create a new text node with a space
            const spaceNode = document.createTextNode(' ');
            const nextTextNode = document.createTextNode('');
            
            highlightElement.parentNode?.insertBefore(spaceNode, highlightElement.nextSibling);
            highlightElement.parentNode?.insertBefore(nextTextNode, spaceNode.nextSibling);

            // Set cursor to the empty text node after the space
            const newRange = document.createRange();
            newRange.setStart(nextTextNode, 0);
            newRange.setEnd(nextTextNode, 0);
            selection.removeAllRanges();
            selection.addRange(newRange);

            // Update content
            const htmlContent = contentRef.current.innerHTML;
            setDisplayText(htmlContent);
            const textWithMarkers = convertHtmlToMarkers(htmlContent);
            setApiText(textWithMarkers);
            onChange(textWithMarkers);

            const newHighlights = extractHighlightsFromText(textWithMarkers);
            onHighlightsChange?.(newHighlights);
          }
        }
      }

      setShowHighlightTooltip(false);
    };

    const convertHtmlToMarkers = (htmlContent: string) => {
      const tempElement = document.createElement('div');
      tempElement.innerHTML = htmlContent;
      let textWithMarkers = '';
      
      const traverseNodes = (node: Node) => {
        // Convert NodeList to Array for proper sequential processing
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE) {
            // Add all text nodes that aren't direct children of highlight spans
            const parent = child.parentNode as HTMLElement;
            if (!parent.classList?.contains('hashtag') && !parent.classList?.contains('mention')) {
              // Preserve whitespace
              textWithMarkers += child.textContent;
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const element = child as HTMLElement;
            if (element.classList.contains('hashtag')) {
              const text = element.textContent?.slice(1); // Remove # symbol
              textWithMarkers += `#{tag:${text}}`;
            } else if (element.classList.contains('mention')) {
              const userId = element.getAttribute('data-user-id');
              const mentionType = element.getAttribute('data-mention-type');
              const username = element.textContent?.slice(1); // Remove @ symbol
              
              // Ensure we're adding the mention marker correctly
              if (mentionType === 'tiktok') {
                textWithMarkers += `@{tiktok:${username}}`;
              } else if (mentionType === 'instagram') {
                textWithMarkers += `@{ig:${username}}`;
              } else if (userId) {
                textWithMarkers += `@{user:${userId}}`;
              }
            } else {
              // For other elements, traverse their children
              traverseNodes(element);
            }
          }
        });
      };

      traverseNodes(tempElement);
      
      // Debug log to check the output
      console.log('HTML content:', htmlContent);
      console.log('Converted markers:', textWithMarkers);
      
      return textWithMarkers;
    };

    const extractHighlightsFromText = (textWithMarkers: string): HighlightData[] => {
      const highlights: HighlightData[] = [];
      const regex = /@\{user:(\d+)\}|@\{tiktok:([^}]+)\}|@\{ig:([^}]+)\}|#\{tag:([^}]+)\}/g;
      let match;

      while ((match = regex.exec(textWithMarkers)) !== null) {
        if (match[1]) { // User mention
          const userId = match[1];
          highlights.push({
            highlight_type: 'mention',
            mention_type: 'user',
            highlight_text: match[0].slice(7, -1), // Remove @{user: and }
            text: match[0],
            data: { user_id: parseInt(userId, 10) },
          });
        } else if (match[2]) { // TikTok mention
          highlights.push({
            highlight_type: 'mention',
            mention_type: 'tiktok',
            highlight_text: match[2],
            text: match[0],
            data: {},
          });
        } else if (match[3]) { // Instagram mention
          highlights.push({
            highlight_type: 'mention',
            mention_type: 'instagram',
            highlight_text: match[3],
            text: match[0],
            data: {},
          });
        } else if (match[4]) { // Hashtag
          const tagText = match[4];
          highlights.push({
            highlight_type: 'hashtag',
            mention_type: '',
            highlight_text: tagText,
            text: match[0],
            data: { tag_id: undefined }, // We don't have tag_id for new hashtags
          });
        }
      }

      return highlights;
    };

    const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
      if (!contentRef.current) return;

      const htmlContent = contentRef.current.innerHTML;
      setDisplayText(htmlContent);

      const textWithMarkers = convertHtmlToMarkers(htmlContent);
      setApiText(textWithMarkers);

      onChange(textWithMarkers);

      const newHighlights = extractHighlightsFromText(textWithMarkers);
      onHighlightsChange?.(newHighlights);

      const selection = window.getSelection();
      if (!selection?.rangeCount) return;

      const range = selection.getRangeAt(0);

      // Don't show tooltip if the cursor is inside a mention or hashtag span
      if (isNodeInsideHighlightSpan(range.startContainer)) {
        setShowHighlightTooltip(false);
        return;
      }

      const textBeforeCursor = getTextBeforeCursor(range);
      const mentionMatch = textBeforeCursor.match(/@([a-zA-Z0-9._-]*)$/);
      const hashtagMatch = textBeforeCursor.match(/#([a-zA-Z0-9._-]*)$/);

      if (mentionMatch) {
        const query = mentionMatch[1];
        setHighlightQuery(query);
        setCurrentHighlightType('mention');
        const coords = getCaretCoordinates(range);
        setHighlightPosition(coords);
        setShowHighlightTooltip(true);
        debouncedSearch(query, 'mention');
      } else if (hashtagMatch) {
        const query = hashtagMatch[1];
        setHighlightQuery(query);
        setCurrentHighlightType('hashtag');
        const coords = getCaretCoordinates(range);
        setHighlightPosition(coords);
        setShowHighlightTooltip(true);
        debouncedSearch(query, 'hashtag');
      } else {
        setShowHighlightTooltip(false);
      }
    };

    // Update contentEditable when displayText changes
    useEffect(() => {
      if (contentRef.current) {
        // Save cursor position
        const savedPosition = saveCursorPosition();

        // Update the innerHTML of the contentEditable div
        contentRef.current.innerHTML = displayText;

        // Restore cursor position
        restoreCursorPosition(savedPosition);
      }
    }, [displayText]);

    const saveCursorPosition = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const preSelectionRange = range.cloneRange();
        preSelectionRange.selectNodeContents(contentRef.current!);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        const start = preSelectionRange.toString().length;

        return start;
      }
      return null;
    };

    const restoreCursorPosition = (savedPosition: number | null) => {
      if (savedPosition !== null && contentRef.current) {
        const selection = window.getSelection();
        const range = document.createRange();
        let charIndex = 0;
        let nodeStack = [contentRef.current];
        let node;
        let foundStart = false;

        while ((node = nodeStack.pop())) {
          if (node.nodeType === Node.TEXT_NODE) {
            const nextCharIndex = charIndex + node.textContent!.length;
            if (!foundStart && savedPosition >= charIndex && savedPosition <= nextCharIndex) {
              range.setStart(node, savedPosition - charIndex);
              foundStart = true;
              break;
            }
            charIndex = nextCharIndex;
          } else {
            let i = node.childNodes.length;
            while (i--) {
              nodeStack.push(node.childNodes[i] as HTMLDivElement);
            }
          }
        }

        if (foundStart) {
          range.collapse(true);
          selection!.removeAllRanges();
          selection!.addRange(range);
        }
      }
    };

    return (
      <div className="content-editable-wrapper">
        <div
          ref={(node) => {
            contentRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          className={`content-editable ${displayText === '' ? 'empty' : ''}`}
          data-placeholder={placeholder}
          contentEditable
          onInput={handleInput}
          suppressContentEditableWarning
        />
        {showHighlightTooltip && (
          currentHighlightType === 'mention' ? (
            <HighlightTooltip
              users={highlightItems.map((item) => ({
                username: item.highlight_text || '',
                profile_picture: item.profile_picture || '',
                account_type: item.mention_type || '',
                user_id: item.user_id,
              }))}
              onSelect={handleHighlightSelect}
              loading={isLoading}
              position={highlightPosition}
              searchText={highlightQuery}
              highlight_type={currentHighlightType}
            />
          ) : (
            <HighlightHashtagTooltip
              hashtags={hashtagItems}
              onSelect={(text, type) => {
                handleHighlightSelect(text, type, '', undefined, undefined);
              }}
              loading={isLoading}
              position={highlightPosition}
              searchText={highlightQuery}
            />
          )
        )}
      </div>
    );
  }
);

export default React.memo(ContentEditable);
